import { ComponentProps } from "react";
import React, { useState } from "react";
import { Pressable } from "react-native";
import { styled } from "@gluestack-style/react";
import { useRouter } from "expo-router";
// import type { IButtonComponentType } from './types';
// import React from "react";

const StyledButton = styled(
	Pressable,
	{
		borderRadius: "$sm",
		backgroundColor: "$primary500",
		flexDirection: "row",
		justifyContent: "center",
		alignItems: "center",
		_text: {
			color: "$text0",
			fontWeight: "$semibold",
		},
		_icon: {
			color: "$text0",
		},
		_spinner: {
			props: {
				color: "$background0",
			},
		},
		variants: {
			action: {
				primary: {
					bg: "$primary500",
					borderColor: "$primary300",
					":hover": {
						bg: "$primary600",
						borderColor: "$primary400",
					},
					":active": {
						bg: "$primary700",
						borderColor: "$primary700",
					},
					_text: {
						color: "$primary600",
						":hover": {
							color: "$primary600",
						},
						":active": {
							color: "$primary700",
						},
					},
					_icon: {
						color: "$primary600",
						":hover": {
							color: "$primary600",
						},
						":active": {
							color: "$primary700",
						},
					},
					_spinner: {
						props: {
							color: "$primary600",
						},
						":hover": {
							props: {
								color: "$primary600",
							},
						},
						":active": {
							props: {
								color: "$primary700",
							},
						},
					},
				},
				secondary: {
					bg: "$secondary500",
					borderColor: "$secondary300",
					":hover": {
						bg: "$secondary600",
						borderColor: "$secondary400",
					},
					":active": {
						bg: "$secondary700",
						borderColor: "$secondary700",
					},
					_text: {
						color: "$secondary600",
						":hover": {
							color: "$secondary600",
						},
						":active": {
							color: "$secondary700",
						},
					},
					_icon: {
						color: "$secondary600",
						":hover": {
							color: "$secondary600",
						},
						":active": {
							color: "$secondary700",
						},
					},
					_spinner: {
						props: {
							color: "$secondary600",
						},
						":hover": {
							props: {
								color: "$secondary600",
							},
						},
						":active": {
							props: {
								color: "$secondary700",
							},
						},
					},
				},
				positive: {
					bg: "$success500",
					borderColor: "$success300",
					":hover": {
						bg: "$success600",
						borderColor: "$success400",
					},
					":active": {
						bg: "$success700",
						borderColor: "$success700",
					},
					_text: {
						color: "$success600",
						":hover": {
							color: "$success600",
						},
						":active": {
							color: "$success700",
						},
					},
					_icon: {
						color: "$success600",
						":hover": {
							color: "$success600",
						},
						":active": {
							color: "$success700",
						},
					},
					_spinner: {
						props: {
							color: "$success600",
						},
						":hover": {
							props: {
								color: "$success600",
							},
						},
						":active": {
							props: {
								color: "$success700",
							},
						},
					},
				},
				negative: {
					bg: "$error500",
					borderColor: "$error300",
					":hover": {
						bg: "$error600",
						borderColor: "$error400",
					},
					":active": {
						bg: "$error700",
						borderColor: "$error700",
					},
					_text: {
						color: "$error600",
						":hover": {
							color: "$error600",
						},
						":active": {
							color: "$error700",
						},
					},
					_icon: {
						color: "$error600",
						":hover": {
							color: "$error600",
						},
						":active": {
							color: "$error700",
						},
					},
					_spinner: {
						props: {
							color: "$error600",
						},
						":hover": {
							props: {
								color: "$error600",
							},
						},
						":active": {
							props: {
								color: "$error700",
							},
						},
					},
				},
				default: {
					bg: "$transparent",
					":hover": {
						bg: "$background50",
					},
					":active": {
						bg: "transparent",
					},
				},
			},
			variant: {
				primary: {
					backgroundColor: "$primary",
					padding: 12,
					_text: {
						color: "$basicwhite",
						":hover": {
							color: "$text0",
						},
						":active": {
							color: "$text0",
						},
					},
				},

				"primary-light": {
					backgroundColor: "$primarylight",
					padding: 12,
					_text: {
						color: "$basicwhite",
						":hover": {
							color: "$text0",
						},
						":active": {
							color: "$text0",
						},
					},
				},

				secondary: {
					borderRadius: 12,
					backgroundColor: "$secondary",
					padding: 12,
					_text: {
						color: "$basicblack",
					},
				},
				outline: {
					backgroundColor: "transparent",
					padding: 12,
					borderColor: "$primarylight",
					borderWidth: 1,
					_text: {
						color: "$primarylight",
						":hover": {
							color: "$text0",
						},
						":active": {
							color: "$text0",
						},
					},
				},
			},
		},
	},
	{
		descendantStyle: ["_text", "_spinner", "_icon"],
		ancestorStyle: ["_button"],
	},
);

interface ButtonProps extends ComponentProps<typeof StyledButton> {
	onHoverIn?: () => void;
	onHoverOut?: () => void;
	isLink?: boolean;
	href?: string;
	target?: string;
	component?: (hovered: boolean) => React.ReactNode;
}

const CustomizedButton = React.forwardRef<any, ButtonProps>((props, ref) => {
	const [hovered, setHovered] = useState(false);
	const [isPressed, setIsPressed] = useState(false);
	const router = useRouter();

	// Extract component prop from props before passing the rest to StyledButton
	const { component, ...restProps } = props;

	if (props.isLink) {
		return (
			<StyledButton
				{...restProps}
				ref={ref}
				onHoverIn={() => {
					setHovered(true);
					if (props.onHoverIn) {
						props.onHoverIn();
					}
				}}
				style={{
					opacity: component ? 1 : isPressed || hovered ? 0.5 : 1,
					// @ts-ignore
					transition: "opacity 0.3s ease",
				}}
				onHoverOut={() => {
					setHovered(false);
					if (props.onHoverOut) {
						props.onHoverOut();
					}
				}}
				onPress={(e) => {
					e.preventDefault();
					if (props.target === "_blank") {
						// Open in a new tab/window
						window.open(props.href, "_blank");
					} else {
						// Navigate within the app
						router.push(props.href ?? "/");
					}
				}}
			>
				{component ? component(hovered) : props.children}
			</StyledButton>
		);
	}
	return (
		<StyledButton
			{...restProps}
			ref={ref}
			onHoverIn={() => {
				setHovered(true);
				if (props.onHoverIn) {
					props.onHoverIn();
				}
			}}
			style={{
				opacity: component ? 1 : isPressed || hovered ? 0.5 : 1,
				// @ts-ignore
				transition: "opacity 0.3s ease",
			}}
			onHoverOut={() => {
				setHovered(false);
				if (props.onHoverOut) {
					props.onHoverOut();
				}
			}}
			onPressIn={() => setIsPressed(true)}
			onPressOut={() => setIsPressed(false)}
		>
			{component ? component(hovered) : props.children}
		</StyledButton>
	);
});

CustomizedButton.displayName = "CustomizedButton";

export const Button = CustomizedButton;
